import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Vault, Users, Wand2, Images, Trophy, GraduationCap, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import logoImage from "@assets/legacyloop-white-trans.png";
import appStoreBadge from "@assets/download-on-app-store.svg";

function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
    (window as any).gtag("event", eventName, params);
  }
}

/** How long each hero photo holds before the slideshow advances. */
const SLIDE_DURATION = 5600;

/**
 * Each slide ships two sizes. `sm` is the original tile-sized export and `lg` is
 * the full-bleed export; the browser picks one via srcSet + sizes="100vw", so a
 * phone never downloads the 1920px file. Widths are the real pixel widths of
 * each file, which is what the `w` descriptor requires to be accurate.
 */
const HERO_SLIDES = [
  { sm: "/hero/hero-1.jpg", smW: 1000, lg: "/hero/hero-1-lg.jpg", lgW: 1920,
    caption: "National Champions", meta: "PGF Nationals",
    alt: "A young softball player smiling as she holds up a national championship trophy" },
  { sm: "/hero/hero-2.jpg", smW: 1000, lg: "/hero/hero-2-lg.jpg", lgW: 1290,
    caption: "In the Circle", meta: "Travel season",
    alt: "A pitcher mid windup in the circle during a softball game" },
  { sm: "/hero/hero-3.jpg", smW: 1000, lg: "/hero/hero-3-lg.jpg", lgW: 1920,
    caption: "Game Night", meta: "Under the lights",
    alt: "A player rising to contest a shot at the rim during a basketball game" },
  { sm: "/hero/hero-4.jpg", smW: 1000, lg: "/hero/hero-4-lg.jpg", lgW: 1920,
    caption: "At the Net", meta: "Club volleyball",
    alt: "Volleyball players set at the net during a match" },
  { sm: "/hero/hero-5.jpg", smW: 667,  lg: "/hero/hero-5-lg.jpg", lgW: 1280,
    caption: "Above the Rim", meta: "Summer circuit",
    alt: "A player elevating for a dunk during a basketball game" },
  { sm: "/hero/hero-6.jpg", smW: 750,  lg: "/hero/hero-6-lg.jpg", lgW: 1440,
    caption: "Meeting Her Idol", meta: "Athletes Unlimited",
    alt: "A young athlete smiling beside a professional player at a meet and greet" },
];

const HERO_FEATURES = [
  { Icon: Images,         title: "Private Vault",     copy: "Photos and video, yours forever" },
  { Icon: Users,          title: "The Loop",          copy: "Grandparents and coaches follow along" },
  { Icon: Trophy,         title: "Milestones",        copy: "First start, first ring, every first" },
  { Icon: GraduationCap,  title: "Recruiting Center", copy: "Ready when those years arrive" },
];

function HeroSection() {
  const { isAuthenticated } = useAuth();
  const heroRef = useRef<HTMLElement>(null);
  const [index, setIndex] = useState(0);
  const [inView, setInView] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  /**
   * Only slide 0 renders an <img> on first paint. Every other slide mounts one
   * step before its turn, so the hero costs a single image at load time instead
   * of six. `loading="lazy"` alone would not do this: the slides are stacked at
   * inset-0, so the browser counts them all as in-viewport and fetches them.
   */
  const [mounted, setMounted] = useState<number[]>([0]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Pause the slideshow whenever the hero is scrolled out of view.
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion || !inView) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), SLIDE_DURATION);
    return () => clearInterval(id);
  }, [reducedMotion, inView]);

  // Warm the next slide so it is decoded before it fades in.
  useEffect(() => {
    const next = (index + 1) % HERO_SLIDES.length;
    setMounted((prev) => (prev.includes(next) ? prev : [...prev, next]));
  }, [index]);

  const active = HERO_SLIDES[index];

  const goTo = (i: number) => {
    setIndex(i);
    trackEvent("hero_slide_select", { slide: i + 1, caption: HERO_SLIDES[i].caption });
  };

  return (
    <section ref={heroRef} className="relative flex flex-col overflow-hidden bg-[#0D1B2A] min-h-[min(92vh,820px)]">
      {/* Photo layer */}
      <div className="absolute inset-0 z-0">
        {HERO_SLIDES.map((slide, i) => (
          <div
            key={slide.sm}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${i === index ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i === index ? undefined : true}
          >
            {mounted.includes(i) && (
              <img
                src={slide.lg}
                srcSet={`${slide.sm} ${slide.smW}w, ${slide.lg} ${slide.lgW}w`}
                sizes="100vw"
                alt={i === index ? slide.alt : ""}
                loading={i === 0 ? "eager" : "lazy"}
                decoding={i === 0 ? "sync" : "async"}
                // The first photo is the LCP element, so it is fetched at high
                // priority; the rest stay low so they never compete with it.
                // React 18 does not map the camelCase prop, so the attribute is
                // spread in lowercase to reach the DOM.
                {...({ fetchpriority: i === 0 ? "high" : "low" } as Record<string, string>)}
                className={`h-full w-full object-cover ${i === index && !reducedMotion ? "hero-kenburns" : ""}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Scrim: keeps the copy legible over any photo */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(13,27,42,.96) 0%, rgba(13,27,42,.88) 34%, rgba(13,27,42,.5) 66%, rgba(13,27,42,.62) 100%), linear-gradient(180deg, rgba(13,27,42,.88) 0%, transparent 32%, rgba(13,27,42,.94) 100%)",
        }}
      />

      {/* Brand bar. Authenticated visitors already get the real app Header, so
          this only renders for logged-out traffic to avoid two nav bars. */}
      {!isAuthenticated && (
        <nav className="relative z-[5] mx-auto flex w-full max-w-[1280px] items-center justify-between gap-5 px-7 pt-6 sm:px-10 lg:px-16">
          <a href="/" aria-label="Legacy Loop home">
            <img src={logoImage} alt="Legacy Loop" className="h-11 w-auto sm:h-[58px]" />
          </a>
          <a
            href="/login?mode=login"
            onClick={() => trackEvent("hero_signin_click", { location: "nav" })}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-[#0D1B2A]/35 px-4 py-2.5 text-[13.5px] font-semibold text-[#e8eef6] backdrop-blur-sm transition-colors hover:border-[#E0A458] hover:text-[#E0A458] sm:px-[22px] sm:text-[14.5px]"
          >
            Sign in
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </nav>
      )}

      {/* Copy */}
      <div className="relative z-[3] mx-auto flex w-full max-w-[1280px] flex-1 items-center px-7 pb-10 pt-[52px] sm:px-10 lg:px-16">
        <div>
          <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[#E0A458]">
            <span className="block h-px w-9 bg-[#E0A458] opacity-80" />
            The Private ESPN of Your Kid&apos;s Journey
          </div>

          <h1 className="font-montserrat my-6 max-w-[16ch] text-[clamp(38px,5.4vw,80px)] font-extrabold leading-[1.02] tracking-[-0.025em]">
            Preserve the Journey.
            <br />
            <span className="bg-gradient-to-r from-[#E0A458] to-[#FDF1DE] bg-clip-text text-transparent">
              Relive Every Moment.
            </span>
          </h1>

          <p className="max-w-[33em] text-[clamp(16px,1.15vw,18.5px)] leading-relaxed text-[#c8d3e0]">
            Every photo, every video, every milestone from your athlete&apos;s career, kept in one
            private timeline that belongs to your family and no one else.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              asChild
              className="bg-[#E0A458] py-6 px-14 text-base font-semibold text-black shadow-lg transition-all hover:bg-[#f0b467] hover:shadow-xl w-full sm:w-auto"
            >
              <a href="/login?mode=signup" onClick={() => trackEvent("hero_cta_click", { cta: "start_free" })}>
                Start for Free
              </a>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="border border-white/25 bg-white/5 py-6 px-10 text-base font-semibold text-white backdrop-blur-sm hover:border-[#E0A458] hover:bg-white/5 hover:text-[#E0A458] w-full sm:w-auto"
            >
              <a href="#how-it-works" onClick={() => trackEvent("hero_cta_click", { cta: "how_it_works" })}>
                See how it works
              </a>
            </Button>
          </div>

          {/* Official Apple "Download on the App Store" badge, links to the live listing. */}
          <div className="mt-4">
            <a
              href="https://apps.apple.com/app/id6795783764"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("hero_cta_click", { cta: "app_store" })}
              aria-label="Download Legacy Loop on the App Store"
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
            </a>
          </div>

          <p className="mt-5 text-xs text-[#8497ab]">
            Free to start. No credit card required. Already have an account?{" "}
            <a
              href="/login?mode=login"
              onClick={() => trackEvent("hero_signin_click", { location: "microcopy" })}
              className="border-b border-[#E0A458]/55 pb-px font-semibold text-[#c8d3e0] transition-colors hover:border-[#E0A458] hover:text-[#E0A458]"
            >
              Sign in
            </a>
            .
          </p>
        </div>
      </div>

      {/* Slide rail */}
      <div className="absolute right-5 top-1/2 z-[4] hidden -translate-y-1/2 flex-col gap-4 lg:flex">
        {HERO_SLIDES.map((slide, i) => (
          <button
            key={slide.sm}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show ${slide.caption}`}
            aria-current={i === index}
            className={`font-montserrat flex items-center gap-2.5 text-xs font-bold tracking-wider transition-colors ${
              i === index ? "text-[#E0A458]" : "text-white/40 hover:text-white/80"
            }`}
          >
            <span className={`block h-px bg-[#E0A458] transition-all duration-300 ${i === index ? "w-8" : "w-0"}`} />
            {String(i + 1).padStart(2, "0")}
          </button>
        ))}
      </div>

      {/* Now showing */}
      <div className="relative z-[3] mx-auto flex w-full max-w-[1280px] items-center gap-3 px-7 pb-3.5 sm:px-10 lg:px-16">
        <span className="h-1.5 w-1.5 flex-none rounded-full bg-[#E0A458]" />
        <span className="text-xs font-semibold tracking-wide text-[#c2cedd]">{active.caption}</span>
        <span className="text-[11px] text-[#4a5b70]">/</span>
        <span className="text-xs tracking-wide text-[#7d8ea3]">{active.meta}</span>
      </div>

      {/* Progress bar, doubling as the divider above the feature strip */}
      <div className="relative z-[3] h-px w-full bg-white/[0.14]">
        {!reducedMotion && inView && (
          <span
            key={index}
            className="hero-progress block h-full bg-[#E0A458]"
            style={{ ["--hero-slide-duration" as any]: `${SLIDE_DURATION}ms` }}
          />
        )}
      </div>

      {/* Feature strip */}
      <div className="relative z-[3] bg-gradient-to-b from-[#0D1B2A]/35 to-[#0D1B2A]/70 backdrop-blur-[3px]">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-5 px-7 py-6 sm:grid-cols-2 sm:px-10 lg:grid-cols-4 lg:gap-7 lg:px-16">
          {HERO_FEATURES.map(({ Icon, title, copy }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] border border-[#E0A458]/30 bg-[#E0A458]/10">
                <Icon className="h-4 w-4 text-[#E0A458]" />
              </div>
              <div>
                <b className="block text-[13.5px] font-bold">{title}</b>
                <p className="text-[11.5px] leading-snug text-slate-400">{copy}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NewLanding() {
  const pricingRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = pricingRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEvent("pricing_section_view");
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white font-sans">

      <HeroSection />

      {/* ── Features ────────────────────────────────────────────── */}
      <section id="how-it-works" className="bg-[#1B263B] py-16 px-6 text-center scroll-mt-4">
        <h2 className="text-3xl font-bold mb-3">Built for the Families in the Stands</h2>
        <p className="text-slate-400 max-w-xl mx-auto mb-10">
          Everything you need to capture, organize, and share your athlete's story. No clutter.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <Card className="bg-[#0D1B2A] border border-[#415A77]/40 text-white text-left h-full">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-xl bg-[#E0A458]/10 flex items-center justify-center mb-4">
                <Vault className="h-5 w-5 text-[#E0A458]" />
              </div>
              <h3 className="font-bold text-lg mb-2">Private Memory Vault</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Every photo, video, and milestone in one secure, private place. Yours forever, not buried in a camera roll.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1B2A] border border-[#E0A458]/40 text-white text-left h-full ring-1 ring-[#E0A458]/10">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-xl bg-[#E0A458]/10 flex items-center justify-center mb-4">
                <Users className="h-5 w-5 text-[#E0A458]" />
              </div>
              <h3 className="font-bold text-lg mb-2">In the Loop Sharing</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Invite grandparents, coaches, and family to see highlights. No account needed. Everyone stays connected to the journey.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1B2A] border border-[#415A77]/40 text-white text-left h-full">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-xl bg-[#E0A458]/10 flex items-center justify-center mb-4">
                <Wand2 className="h-5 w-5 text-[#E0A458]" />
              </div>
              <h3 className="font-bold text-lg mb-2">Recruiting Ready</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                NCAA contact calendars, an AI coach email writer, and a college interest tracker. When the recruiting years arrive, the journey is already organized.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── Emotional Quote ──────────────────────────────────────── */}
      <section className="py-24 bg-[#0D1B2A] px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B263B]/10 to-transparent pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          <div className="text-7xl text-[#E0A458]/15 font-serif leading-none mb-2 select-none">"</div>
          <blockquote className="text-2xl md:text-4xl font-bold text-white leading-snug mb-6">
            She won't remember every game.<br />
            <span className="text-[#E0A458]">You will.</span>
          </blockquote>
          <p className="text-slate-400 text-base max-w-md mx-auto">
            LegacyLoop is the place where those moments live, long after the season ends.
          </p>
        </div>
      </section>

      {/* ── Founder note ─────────────────────────────────────────── */}
      <section className="py-16 bg-[#1B263B] px-6">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#E0A458] text-xs font-semibold uppercase tracking-widest mb-4">
            Built by a sports parent
          </p>
          <p className="text-xl text-slate-200 leading-relaxed mb-4">
            "She's been training since she was 9. The games, the tears, the
            wins: thousands of moments were rotting in my camera roll. I built
            LegacyLoop so my family would never lose the journey. Now it's
            yours too."
          </p>
          <p className="text-sm text-slate-400">The founder, a softball dad in Phoenix</p>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────── */}
      <section className="py-20 bg-[#0D1B2A] text-center px-6">
        <h2 className="text-3xl font-bold mb-2">What Parents Are Saying</h2>
        <p className="text-slate-400 mb-12 text-sm">Real families. Real moments.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <blockquote className="bg-[#1B263B] border border-[#415A77]/40 text-white p-6 rounded-2xl shadow text-left">
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-[#E0A458] text-sm">★</span>
              ))}
            </div>
            <p className="text-slate-200 leading-relaxed mb-4 italic">
              "This is like our family ESPN. We use it every weekend."
            </p>
            <footer className="text-sm text-slate-400">Jordan W., softball dad</footer>
          </blockquote>

          <blockquote className="bg-[#1B263B] border border-[#415A77]/40 text-white p-6 rounded-2xl shadow text-left">
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-[#E0A458] text-sm">★</span>
              ))}
            </div>
            <p className="text-slate-200 leading-relaxed mb-4 italic">
              "I cried watching the highlight it made of her first tournament."
            </p>
            <footer className="text-sm text-slate-400">Tanya B., volleyball mom</footer>
          </blockquote>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────── */}
      <section ref={pricingRef} className="bg-[#1B263B] py-16 px-6 text-center">
        <h2 className="text-3xl font-bold mb-3">Simple, Honest Pricing</h2>
        <p className="text-slate-400 max-w-xl mx-auto mb-8">Start free. Upgrade when you're ready.</p>

        {/* Founding family offer */}
        <div className="max-w-2xl mx-auto mb-10 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] border border-[#E0A458]/40 rounded-2xl px-6 py-5">
          <p className="text-[#E0A458] text-xs font-semibold uppercase tracking-widest mb-1">
            Founding Family Offer
          </p>
          <p className="font-montserrat font-bold text-lg text-white mb-1">Save 40% on your first year</p>
          <p className="text-slate-300 text-sm">
            Use code <span className="text-[#E0A458] font-semibold">FOUNDING40</span> at checkout on Loop Family or Loop Recruit. Our thank-you to the families joining us early.
          </p>
        </div>

        {/* items-stretch gives all cards equal height in each row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto items-stretch">
          {/* Free */}
          <Card className="bg-[#0D1B2A] text-white border border-[#415A77]/50 flex flex-col">
            <CardContent className="p-6 flex flex-col flex-1">
              <h3 className="text-lg font-bold mb-1">Free Forever</h3>
              <p className="text-3xl font-bold text-[#E0A458] mb-0.5">$0</p>
              <p className="text-xs text-slate-400 mb-4">Always free</p>
              <ul className="text-sm text-slate-300 space-y-2 text-left mb-6 flex-1">
                <li>✓ 1 athlete profile</li>
                <li>✓ 500MB storage</li>
                <li>✓ Timeline &amp; milestones</li>
                <li className="text-slate-500 line-through">No watermarks</li>
                <li className="text-slate-500 line-through">AI features</li>
              </ul>
              <Button asChild className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold w-full mt-auto">
                <a href="/login?mode=signup" onClick={() => trackEvent("pricing_cta_click", { plan: "free" })}>Get Started</a>
              </Button>
            </CardContent>
          </Card>

          {/* Loop Family — most popular */}
          <Card className="bg-[#0D1B2A] text-white border-2 border-[#E0A458] flex flex-col relative">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
              <Badge className="bg-[#E0A458] text-black font-bold px-3 py-1 text-xs">Most Popular</Badge>
            </div>
            <CardContent className="p-6 flex flex-col flex-1 pt-8">
              <h3 className="text-lg font-bold mb-1">Loop Family</h3>
              <p className="text-3xl font-bold text-[#E0A458] mb-0.5">
                $99<span className="text-base font-normal text-slate-400">/yr</span>
              </p>
              <p className="text-xs text-slate-400 mb-4">or $12.99/mo · The whole journey, one home</p>
              <ul className="text-sm text-slate-300 space-y-2 text-left mb-6 flex-1">
                <li>✓ 2 athlete profiles</li>
                <li>✓ 1TB storage</li>
                <li>✓ No watermarks</li>
                <li>✓ HD video support</li>
                <li>✓ AI journaling prompts</li>
                <li>✓ The Loop sharing &amp; Loop Links</li>
              </ul>
              <Button asChild className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold w-full mt-auto">
                <a href="/login?mode=signup" onClick={() => trackEvent("pricing_cta_click", { plan: "loop_family" })}>Get Started</a>
              </Button>
            </CardContent>
          </Card>

          {/* Loop Recruit */}
          <Card className="bg-[#0D1B2A] text-white border border-[#415A77]/50 flex flex-col">
            <CardContent className="p-6 flex flex-col flex-1">
              <h3 className="text-lg font-bold mb-1">Loop Recruit</h3>
              <p className="text-3xl font-bold text-[#E0A458] mb-0.5">
                $199<span className="text-base font-normal text-slate-400">/yr</span>
              </p>
              <p className="text-xs text-slate-400 mb-4">For families on the recruiting path</p>
              <ul className="text-sm text-slate-300 space-y-2 text-left mb-6 flex-1">
                <li>✓ Everything in Loop Family</li>
                <li>✓ 2TB storage</li>
                <li>✓ Full Recruiting Center</li>
                <li>✓ AI coach email writer</li>
                <li>✓ College interest tracker</li>
                <li>✓ Priority features</li>
              </ul>
              <Button asChild className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold w-full mt-auto">
                <a href="/login?mode=signup" onClick={() => trackEvent("pricing_cta_click", { plan: "loop_recruit" })}>Get Started</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────── */}
      <section className="py-16 bg-[#1B263B] text-center px-6">
        <div className="max-w-2xl mx-auto py-10 px-8 bg-gradient-to-r from-[#0D1B2A] to-[#263852] rounded-2xl shadow-xl border border-[#415A77]/30">
          <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-[#E0A458] to-[#fff] text-transparent bg-clip-text">
            Ready to be a part of your athlete's legacy?
          </h2>
          <p className="text-slate-300 max-w-md mx-auto mb-6 text-sm">
            Join families already preserving the moments that matter most.
          </p>
          <Button
            asChild
            className="bg-[#E0A458] text-black hover:bg-[#f0b467] py-6 px-14 font-semibold text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            <a href="/login?mode=signup">Start for Free</a>
          </Button>
          <p className="text-xs text-slate-500 mt-4">Free to start. No credit card required.</p>
          <div className="mt-6 flex justify-center">
            <a
              href="https://apps.apple.com/app/id6795783764"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("footer_cta_click", { cta: "app_store" })}
              aria-label="Download Legacy Loop on the App Store"
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
